import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import VoiceInputButton from '../VoiceInputButton';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  useMediaQuery,
  useTheme,
  Paper,
  Chip,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Badge,
  Tabs,
  Tab,
  InputAdornment,
  Avatar,
  CircularProgress,
  Snackbar,
  Tooltip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Place as PlaceIcon,
  Search as SearchIcon,
  Public as GlobeIcon,
  Send as SendIcon,
  Menu as MenuIcon,
  VolumeUp as VolumeUpIcon,
  Stop as StopIcon,
  ContentCopy as CopyIcon,
  Quiz as QuizIcon,
  EmojiEvents as TrophyIcon,
  CheckCircle as CheckIcon,
  Cancel as WrongIcon,
  NavigateNext as NextIcon,
  Replay as RetryIcon,
  Map as MapIcon,
  Satellite as SatelliteIcon,
  Terrain as TerrainIcon,
  Layers as LayersIcon,
  MyLocation as MyLocationIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { callLLM } from '../../services/llmService';
import { markdownToHtml } from '../../utils/markdownRenderer';
import { speakText, stopSpeaking } from '../../services/voiceService';

// Fix Leaflet default marker icon (broken in webpack/CRA builds)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom red marker for quiz target
const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Custom green marker for correct answer
const greenIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Tile layer configurations
const TILE_LAYERS = {
  street: {
    label: 'Street',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    icon: <MapIcon sx={{ fontSize: 16 }} />,
  },
  satellite: {
    label: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    icon: <SatelliteIcon sx={{ fontSize: 16 }} />,
  },
  terrain: {
    label: 'Terrain',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
    icon: <TerrainIcon sx={{ fontSize: 16 }} />,
  },
  topo: {
    label: 'Topographic',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom, Intermap',
    icon: <LayersIcon sx={{ fontSize: 16 }} />,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Map controller — flies to location when center/zoom changes
// ─────────────────────────────────────────────────────────────────────────────
function MapFlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo([center.lat, center.lng], zoom, { duration: 1.2 });
  }, [center, zoom, map]);
  return null;
}

// Click handler for quiz
function QuizClickHandler({ onClick }) {
  useMapEvents({ click: (e) => onClick(e.latlng) });
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Vyonn Globe Icon component
// ─────────────────────────────────────────────────────────────────────────────
function VyonnGlobeIcon({ size = 40 }) {
  return (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      badgeContent={
        <Box
          sx={{
            width: size * 0.5,
            height: size * 0.5,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0984e3 0%, #0652dd 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(9,132,227,0.4)'
          }}
        >
          <GlobeIcon sx={{ fontSize: size * 0.3, color: 'white' }} />
        </Box>
      }
    >
      <Box
        sx={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          component="img"
          src="/vyonn.png"
          alt="Vyonn"
          sx={{
            width: size * 0.85,
            height: size * 0.85,
            filter: 'brightness(0) invert(1) brightness(1.8)',
            objectFit: 'contain'
          }}
        />
      </Box>
    </Badge>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Map Pointing Quiz — Region-scoped with states, rivers, cities, provinces
// ─────────────────────────────────────────────────────────────────────────────

// Region definitions: bounds + category-specific quiz items
const QUIZ_REGIONS = {
  // ── World ──────────────────────────────────────────────────────────────────
  world: {
    label: 'World',
    type: 'world',
    bounds: [[-60, -180], [80, 180]],
    categories: {
      landmarks: {
        label: 'Landmarks',
        items: [
          { name: 'Great Wall of China', lat: 40.43, lng: 116.57, hint: 'Eastern Asia', accept: 500 },
          { name: 'Pyramids of Giza', lat: 29.98, lng: 31.13, hint: 'North Africa', accept: 400 },
          { name: 'Machu Picchu', lat: -13.16, lng: -72.55, hint: 'South America', accept: 400 },
          { name: 'Taj Mahal', lat: 27.18, lng: 78.04, hint: 'South Asia', accept: 400 },
          { name: 'Colosseum', lat: 41.89, lng: 12.49, hint: 'Southern Europe', accept: 300 },
          { name: 'Eiffel Tower', lat: 48.86, lng: 2.29, hint: 'Western Europe', accept: 300 },
          { name: 'Statue of Liberty', lat: 40.69, lng: -74.04, hint: 'North America', accept: 300 },
          { name: 'Christ the Redeemer', lat: -22.95, lng: -43.17, hint: 'South America', accept: 400 },
          { name: 'Sydney Opera House', lat: -33.86, lng: 151.22, hint: 'Oceania', accept: 400 },
          { name: 'Petra', lat: 30.33, lng: 35.44, hint: 'Middle East', accept: 400 },
        ],
      },
      capitals: {
        label: 'Capitals',
        items: [
          { name: 'Tokyo', lat: 35.68, lng: 139.69, hint: 'Capital of Japan', accept: 300 },
          { name: 'London', lat: 51.51, lng: -0.13, hint: 'Capital of United Kingdom', accept: 300 },
          { name: 'Washington D.C.', lat: 38.91, lng: -77.04, hint: 'Capital of USA', accept: 300 },
          { name: 'Canberra', lat: -35.28, lng: 149.13, hint: 'Capital of Australia', accept: 400 },
          { name: 'Brasilia', lat: -15.79, lng: -47.88, hint: 'Capital of Brazil', accept: 400 },
          { name: 'Cairo', lat: 30.04, lng: 31.24, hint: 'Capital of Egypt', accept: 300 },
          { name: 'Moscow', lat: 55.76, lng: 37.62, hint: 'Capital of Russia', accept: 400 },
          { name: 'Beijing', lat: 39.91, lng: 116.40, hint: 'Capital of China', accept: 300 },
          { name: 'Ottawa', lat: 45.42, lng: -75.70, hint: 'Capital of Canada', accept: 400 },
          { name: 'Nairobi', lat: -1.29, lng: 36.82, hint: 'Capital of Kenya', accept: 400 },
        ],
      },
      rivers: {
        label: 'Rivers & Mountains',
        items: [
          { name: 'Mount Everest', lat: 27.99, lng: 86.93, hint: 'Himalayas, Nepal/China border', accept: 400 },
          { name: 'Nile River Delta', lat: 30.87, lng: 31.02, hint: 'Longest river in Africa', accept: 500 },
          { name: 'Amazon River Mouth', lat: -0.50, lng: -49.30, hint: 'Largest river by volume', accept: 500 },
          { name: 'Mount Kilimanjaro', lat: -3.07, lng: 37.35, hint: 'Tallest peak in Africa', accept: 400 },
          { name: 'Niagara Falls', lat: 43.08, lng: -79.07, hint: 'USA/Canada border', accept: 300 },
          { name: 'Mount Fuji', lat: 35.36, lng: 138.73, hint: 'Iconic Japanese volcano', accept: 300 },
          { name: 'Victoria Falls', lat: -17.92, lng: 25.86, hint: 'Zambia/Zimbabwe border', accept: 500 },
          { name: 'Danube at Budapest', lat: 47.50, lng: 19.04, hint: 'Major European river', accept: 400 },
        ],
      },
    },
  },

  // ── Continents ─────────────────────────────────────────────────────────────
  asia: {
    label: 'Asia',
    type: 'continent',
    bounds: [[0, 25], [60, 150]],
    categories: {
      capitals: {
        label: 'Capitals',
        items: [
          { name: 'Tokyo', lat: 35.68, lng: 139.69, hint: 'Japan', accept: 200 },
          { name: 'Beijing', lat: 39.91, lng: 116.40, hint: 'China', accept: 200 },
          { name: 'Seoul', lat: 37.57, lng: 126.98, hint: 'South Korea', accept: 200 },
          { name: 'Bangkok', lat: 13.76, lng: 100.50, hint: 'Thailand', accept: 200 },
          { name: 'Hanoi', lat: 21.03, lng: 105.85, hint: 'Vietnam', accept: 200 },
          { name: 'Jakarta', lat: -6.21, lng: 106.85, hint: 'Indonesia', accept: 250 },
          { name: 'Kuala Lumpur', lat: 3.14, lng: 101.69, hint: 'Malaysia', accept: 200 },
          { name: 'Islamabad', lat: 33.69, lng: 73.04, hint: 'Pakistan', accept: 200 },
          { name: 'Dhaka', lat: 23.81, lng: 90.41, hint: 'Bangladesh', accept: 200 },
          { name: 'Kathmandu', lat: 27.72, lng: 85.32, hint: 'Nepal', accept: 150 },
        ],
      },
      rivers: {
        label: 'Rivers & Mountains',
        items: [
          { name: 'Mount Everest', lat: 27.99, lng: 86.93, hint: 'Highest peak in the world', accept: 200 },
          { name: 'Ganges at Varanasi', lat: 25.28, lng: 83.01, hint: 'Sacred river in India', accept: 200 },
          { name: 'Yangtze River Mouth', lat: 31.39, lng: 121.97, hint: 'Longest river in Asia', accept: 300 },
          { name: 'Mekong Delta', lat: 10.04, lng: 105.79, hint: 'Southeast Asia', accept: 300 },
          { name: 'Mount Fuji', lat: 35.36, lng: 138.73, hint: 'Iconic Japanese volcano', accept: 200 },
          { name: 'Dead Sea', lat: 31.50, lng: 35.50, hint: 'Lowest point on Earth', accept: 250 },
          { name: 'Indus River at Sukkur', lat: 27.70, lng: 68.86, hint: 'Pakistan, cradle of civilization', accept: 250 },
          { name: 'Lake Baikal', lat: 53.50, lng: 108.00, hint: 'Deepest lake in the world (Russia)', accept: 300 },
        ],
      },
    },
  },
  europe: {
    label: 'Europe',
    type: 'continent',
    bounds: [[35, -12], [72, 45]],
    categories: {
      capitals: {
        label: 'Capitals',
        items: [
          { name: 'London', lat: 51.51, lng: -0.13, hint: 'United Kingdom', accept: 150 },
          { name: 'Paris', lat: 48.86, lng: 2.35, hint: 'France', accept: 150 },
          { name: 'Berlin', lat: 52.52, lng: 13.41, hint: 'Germany', accept: 150 },
          { name: 'Rome', lat: 41.90, lng: 12.50, hint: 'Italy', accept: 150 },
          { name: 'Madrid', lat: 40.42, lng: -3.70, hint: 'Spain', accept: 150 },
          { name: 'Athens', lat: 37.98, lng: 23.73, hint: 'Greece', accept: 150 },
          { name: 'Stockholm', lat: 59.33, lng: 18.07, hint: 'Sweden', accept: 150 },
          { name: 'Warsaw', lat: 52.23, lng: 21.01, hint: 'Poland', accept: 150 },
          { name: 'Lisbon', lat: 38.72, lng: -9.14, hint: 'Portugal', accept: 150 },
          { name: 'Vienna', lat: 48.21, lng: 16.37, hint: 'Austria', accept: 150 },
        ],
      },
      rivers: {
        label: 'Rivers & Mountains',
        items: [
          { name: 'Danube at Budapest', lat: 47.50, lng: 19.04, hint: 'Second longest river in Europe', accept: 150 },
          { name: 'Rhine at Cologne', lat: 50.94, lng: 6.96, hint: 'Major western European river', accept: 150 },
          { name: 'Thames at London', lat: 51.50, lng: -0.12, hint: 'Flows through the UK capital', accept: 120 },
          { name: 'Mont Blanc', lat: 45.83, lng: 6.87, hint: 'Highest peak in the Alps', accept: 120 },
          { name: 'Matterhorn', lat: 45.98, lng: 7.66, hint: 'Iconic Alpine peak, Switzerland/Italy', accept: 120 },
          { name: 'Volga at Kazan', lat: 55.79, lng: 49.11, hint: 'Longest river in Europe', accept: 200 },
          { name: 'Seine at Paris', lat: 48.86, lng: 2.35, hint: 'Famous French river', accept: 120 },
          { name: 'Mount Etna', lat: 37.75, lng: 14.99, hint: 'Active volcano in Sicily', accept: 150 },
        ],
      },
    },
  },
  africa: {
    label: 'Africa',
    type: 'continent',
    bounds: [[-36, -20], [38, 52]],
    categories: {
      capitals: {
        label: 'Capitals',
        items: [
          { name: 'Cairo', lat: 30.04, lng: 31.24, hint: 'Egypt', accept: 200 },
          { name: 'Nairobi', lat: -1.29, lng: 36.82, hint: 'Kenya', accept: 200 },
          { name: 'Lagos', lat: 6.52, lng: 3.38, hint: 'Largest city in Nigeria', accept: 200 },
          { name: 'Cape Town', lat: -33.93, lng: 18.42, hint: 'South Africa (legislative capital)', accept: 200 },
          { name: 'Addis Ababa', lat: 9.02, lng: 38.75, hint: 'Ethiopia', accept: 200 },
          { name: 'Accra', lat: 5.56, lng: -0.19, hint: 'Ghana', accept: 200 },
          { name: 'Casablanca', lat: 33.59, lng: -7.62, hint: 'Largest city in Morocco', accept: 200 },
          { name: 'Dar es Salaam', lat: -6.79, lng: 39.28, hint: 'Tanzania', accept: 250 },
          { name: 'Algiers', lat: 36.75, lng: 3.06, hint: 'Algeria', accept: 200 },
          { name: 'Khartoum', lat: 15.50, lng: 32.56, hint: 'Sudan, where the Niles meet', accept: 250 },
        ],
      },
      rivers: {
        label: 'Rivers & Mountains',
        items: [
          { name: 'Nile River at Aswan', lat: 24.09, lng: 32.90, hint: 'Longest river in Africa', accept: 250 },
          { name: 'Victoria Falls', lat: -17.92, lng: 25.86, hint: 'Zambia/Zimbabwe border', accept: 300 },
          { name: 'Mount Kilimanjaro', lat: -3.07, lng: 37.35, hint: 'Tallest peak in Africa', accept: 200 },
          { name: 'Congo River at Kinshasa', lat: -4.32, lng: 15.31, hint: 'Second longest river in Africa', accept: 300 },
          { name: 'Lake Victoria', lat: -1.27, lng: 32.86, hint: 'Largest lake in Africa', accept: 300 },
          { name: 'Niger River at Bamako', lat: 12.65, lng: -8.00, hint: 'Major West African river', accept: 300 },
          { name: 'Atlas Mountains', lat: 31.07, lng: -7.91, hint: 'Mountain range in NW Africa', accept: 300 },
          { name: 'Zambezi at Livingstone', lat: -17.85, lng: 25.87, hint: 'Fourth longest African river', accept: 300 },
        ],
      },
    },
  },
  'north-america': {
    label: 'North America',
    type: 'continent',
    bounds: [[10, -170], [72, -50]],
    categories: {
      capitals: {
        label: 'Capitals & Cities',
        items: [
          { name: 'Washington D.C.', lat: 38.91, lng: -77.04, hint: 'Capital of USA', accept: 200 },
          { name: 'Ottawa', lat: 45.42, lng: -75.70, hint: 'Capital of Canada', accept: 200 },
          { name: 'Mexico City', lat: 19.43, lng: -99.13, hint: 'Capital of Mexico', accept: 200 },
          { name: 'Havana', lat: 23.11, lng: -82.37, hint: 'Capital of Cuba', accept: 200 },
          { name: 'New York City', lat: 40.71, lng: -74.01, hint: 'Largest city in USA', accept: 200 },
          { name: 'Los Angeles', lat: 34.05, lng: -118.24, hint: 'West coast, California', accept: 200 },
          { name: 'Toronto', lat: 43.65, lng: -79.38, hint: 'Largest city in Canada', accept: 200 },
          { name: 'Chicago', lat: 41.88, lng: -87.63, hint: 'On Lake Michigan, Illinois', accept: 200 },
          { name: 'Guatemala City', lat: 14.63, lng: -90.51, hint: 'Capital of Guatemala', accept: 250 },
          { name: 'Panama City', lat: 8.98, lng: -79.52, hint: 'Capital of Panama', accept: 250 },
        ],
      },
      rivers: {
        label: 'Rivers & Mountains',
        items: [
          { name: 'Mississippi River at Memphis', lat: 35.15, lng: -90.05, hint: 'Longest river system in North America', accept: 200 },
          { name: 'Niagara Falls', lat: 43.08, lng: -79.07, hint: 'USA/Canada border', accept: 200 },
          { name: 'Grand Canyon', lat: 36.11, lng: -112.11, hint: 'Arizona, carved by the Colorado River', accept: 200 },
          { name: 'Rocky Mountains at Denver', lat: 39.74, lng: -104.99, hint: 'Major mountain range', accept: 250 },
          { name: 'Great Lakes — Lake Superior', lat: 47.70, lng: -87.50, hint: 'Largest freshwater lake by area', accept: 300 },
          { name: 'Colorado River at Hoover Dam', lat: 36.02, lng: -114.74, hint: 'Major river of the Southwest', accept: 200 },
          { name: 'Mount Denali', lat: 63.07, lng: -151.01, hint: 'Tallest peak in North America (Alaska)', accept: 300 },
          { name: 'Rio Grande at El Paso', lat: 31.76, lng: -106.49, hint: 'USA/Mexico border river', accept: 200 },
        ],
      },
    },
  },
  'south-america': {
    label: 'South America',
    type: 'continent',
    bounds: [[-56, -82], [13, -34]],
    categories: {
      capitals: {
        label: 'Capitals & Cities',
        items: [
          { name: 'Brasilia', lat: -15.79, lng: -47.88, hint: 'Capital of Brazil', accept: 200 },
          { name: 'Buenos Aires', lat: -34.60, lng: -58.38, hint: 'Capital of Argentina', accept: 200 },
          { name: 'Lima', lat: -12.05, lng: -77.04, hint: 'Capital of Peru', accept: 200 },
          { name: 'Bogota', lat: 4.71, lng: -74.07, hint: 'Capital of Colombia', accept: 200 },
          { name: 'Santiago', lat: -33.45, lng: -70.67, hint: 'Capital of Chile', accept: 200 },
          { name: 'Quito', lat: -0.18, lng: -78.47, hint: 'Capital of Ecuador', accept: 200 },
          { name: 'Caracas', lat: 10.48, lng: -66.90, hint: 'Capital of Venezuela', accept: 200 },
          { name: 'Montevideo', lat: -34.88, lng: -56.17, hint: 'Capital of Uruguay', accept: 200 },
          { name: 'La Paz', lat: -16.49, lng: -68.12, hint: 'Administrative capital of Bolivia', accept: 200 },
          { name: 'Asuncion', lat: -25.26, lng: -57.58, hint: 'Capital of Paraguay', accept: 250 },
        ],
      },
      rivers: {
        label: 'Rivers & Mountains',
        items: [
          { name: 'Amazon River Mouth', lat: -0.50, lng: -49.30, hint: 'Largest river by volume', accept: 300 },
          { name: 'Iguazu Falls', lat: -25.69, lng: -54.44, hint: 'Brazil/Argentina border', accept: 200 },
          { name: 'Aconcagua', lat: -32.65, lng: -70.01, hint: 'Highest peak in the Americas (Argentina)', accept: 200 },
          { name: 'Angel Falls', lat: 5.97, lng: -62.54, hint: 'Tallest waterfall in the world (Venezuela)', accept: 300 },
          { name: 'Lake Titicaca', lat: -15.84, lng: -69.33, hint: 'Highest navigable lake (Peru/Bolivia)', accept: 250 },
          { name: 'Orinoco River Delta', lat: 8.56, lng: -60.73, hint: 'Major Venezuelan river', accept: 300 },
          { name: 'Parana River at Rosario', lat: -32.95, lng: -60.64, hint: 'Second longest river in South America', accept: 250 },
          { name: 'Atacama Desert', lat: -24.50, lng: -69.25, hint: 'Driest desert on Earth (Chile)', accept: 300 },
        ],
      },
    },
  },

  // ── Countries ──────────────────────────────────────────────────────────────
  india: {
    label: 'India',
    type: 'country',
    bounds: [[6, 68], [37, 98]],
    categories: {
      states: {
        label: 'States & Union Territories',
        items: [
          { name: 'Rajasthan', lat: 26.90, lng: 73.80, hint: 'Largest state by area, NW India', accept: 150 },
          { name: 'Maharashtra', lat: 19.60, lng: 75.50, hint: 'Home to Mumbai', accept: 150 },
          { name: 'Tamil Nadu', lat: 11.00, lng: 78.50, hint: 'Southern tip, Bay of Bengal coast', accept: 130 },
          { name: 'Karnataka', lat: 14.50, lng: 75.70, hint: 'Home to Bangalore', accept: 130 },
          { name: 'Uttar Pradesh', lat: 27.00, lng: 80.50, hint: 'Most populous state', accept: 130 },
          { name: 'West Bengal', lat: 23.00, lng: 87.80, hint: 'Home to Kolkata, eastern India', accept: 130 },
          { name: 'Gujarat', lat: 22.30, lng: 71.80, hint: 'Western coast, birthplace of Mahatma Gandhi', accept: 130 },
          { name: 'Kerala', lat: 10.50, lng: 76.30, hint: 'Southwest coast, "God\'s Own Country"', accept: 100 },
          { name: 'Punjab', lat: 31.00, lng: 75.35, hint: 'NW India, land of five rivers', accept: 100 },
          { name: 'Odisha', lat: 20.50, lng: 84.00, hint: 'East coast, known for Jagannath Temple', accept: 130 },
          { name: 'Madhya Pradesh', lat: 23.50, lng: 78.50, hint: 'Heart of India, largest by area in central India', accept: 150 },
          { name: 'Telangana', lat: 17.80, lng: 79.00, hint: 'Home to Hyderabad', accept: 100 },
          { name: 'Bihar', lat: 25.60, lng: 85.10, hint: 'Eastern India, birthplace of Buddhism', accept: 100 },
          { name: 'Assam', lat: 26.20, lng: 92.90, hint: 'Northeast India, famous for tea', accept: 130 },
          { name: 'Goa', lat: 15.30, lng: 74.00, hint: 'Smallest state, west coast', accept: 80 },
        ],
      },
      cities: {
        label: 'Major Cities',
        items: [
          { name: 'New Delhi', lat: 28.61, lng: 77.21, hint: 'Capital of India', accept: 80 },
          { name: 'Mumbai', lat: 19.08, lng: 72.88, hint: 'Financial capital, Maharashtra', accept: 80 },
          { name: 'Kolkata', lat: 22.57, lng: 88.36, hint: 'Cultural capital, West Bengal', accept: 80 },
          { name: 'Chennai', lat: 13.08, lng: 80.27, hint: 'Gateway to South India', accept: 80 },
          { name: 'Hyderabad', lat: 17.39, lng: 78.49, hint: 'City of Pearls, Telangana', accept: 80 },
          { name: 'Bangalore', lat: 12.97, lng: 77.59, hint: 'Silicon Valley of India', accept: 80 },
          { name: 'Jaipur', lat: 26.91, lng: 75.79, hint: 'Pink City, Rajasthan', accept: 80 },
          { name: 'Varanasi', lat: 25.32, lng: 82.99, hint: 'Oldest city, on the Ganges', accept: 80 },
          { name: 'Lucknow', lat: 26.85, lng: 80.95, hint: 'City of Nawabs, Uttar Pradesh', accept: 80 },
          { name: 'Ahmedabad', lat: 23.02, lng: 72.57, hint: 'Largest city in Gujarat', accept: 80 },
          { name: 'Pune', lat: 18.52, lng: 73.86, hint: 'Oxford of the East, Maharashtra', accept: 80 },
          { name: 'Chandigarh', lat: 30.73, lng: 76.78, hint: 'Planned city, Punjab/Haryana capital', accept: 80 },
        ],
      },
      rivers: {
        label: 'Rivers & Mountains',
        items: [
          { name: 'Ganges at Varanasi', lat: 25.28, lng: 83.01, hint: 'Holiest river in Hinduism', accept: 80 },
          { name: 'Yamuna at Delhi', lat: 28.63, lng: 77.24, hint: 'Tributary of Ganges, flows through capital', accept: 80 },
          { name: 'Brahmaputra at Guwahati', lat: 26.14, lng: 91.74, hint: 'Major river of NE India', accept: 100 },
          { name: 'Godavari at Nashik', lat: 20.00, lng: 73.78, hint: 'Longest river in peninsular India', accept: 100 },
          { name: 'Krishna River at Vijayawada', lat: 16.51, lng: 80.65, hint: 'Major river of South India', accept: 100 },
          { name: 'Narmada at Jabalpur', lat: 23.17, lng: 79.95, hint: 'Flows westward into Arabian Sea', accept: 100 },
          { name: 'Kanchenjunga', lat: 27.70, lng: 88.15, hint: 'Third highest peak in the world, Sikkim', accept: 100 },
          { name: 'Western Ghats at Munnar', lat: 10.09, lng: 77.06, hint: 'Mountain range along the west coast', accept: 100 },
          { name: 'Sundarbans', lat: 21.95, lng: 89.18, hint: 'Largest mangrove forest, Ganges delta', accept: 120 },
          { name: 'Cauvery at Mysore', lat: 12.30, lng: 76.66, hint: 'Major river of Karnataka/Tamil Nadu', accept: 80 },
        ],
      },
    },
  },
  usa: {
    label: 'United States',
    type: 'country',
    bounds: [[24, -125], [50, -66]],
    categories: {
      states: {
        label: 'States',
        items: [
          { name: 'California', lat: 36.78, lng: -119.42, hint: 'West coast, most populous state', accept: 200 },
          { name: 'Texas', lat: 31.97, lng: -99.90, hint: 'Largest state in lower 48', accept: 200 },
          { name: 'Florida', lat: 27.66, lng: -81.52, hint: 'Sunshine State, SE peninsula', accept: 150 },
          { name: 'New York', lat: 42.17, lng: -74.95, hint: 'Empire State, NE coast', accept: 150 },
          { name: 'Illinois', lat: 40.63, lng: -89.40, hint: 'Home to Chicago, Midwest', accept: 150 },
          { name: 'Pennsylvania', lat: 41.20, lng: -77.19, hint: 'Keystone State, NE', accept: 150 },
          { name: 'Ohio', lat: 40.42, lng: -82.91, hint: 'Heart of the Midwest', accept: 130 },
          { name: 'Georgia', lat: 32.16, lng: -82.90, hint: 'Peach State, home to Atlanta', accept: 150 },
          { name: 'Michigan', lat: 44.31, lng: -85.60, hint: 'Great Lakes state', accept: 150 },
          { name: 'Colorado', lat: 39.06, lng: -105.31, hint: 'Rocky Mountain state', accept: 150 },
          { name: 'Washington', lat: 47.75, lng: -120.74, hint: 'Pacific NW, home to Seattle', accept: 150 },
          { name: 'Montana', lat: 46.88, lng: -110.36, hint: 'Big Sky Country, NW', accept: 200 },
        ],
      },
      cities: {
        label: 'Major Cities',
        items: [
          { name: 'New York City', lat: 40.71, lng: -74.01, hint: 'Largest city, East coast', accept: 100 },
          { name: 'Los Angeles', lat: 34.05, lng: -118.24, hint: 'Second largest, California', accept: 100 },
          { name: 'Chicago', lat: 41.88, lng: -87.63, hint: 'Windy City, on Lake Michigan', accept: 100 },
          { name: 'Houston', lat: 29.76, lng: -95.37, hint: 'Largest city in Texas', accept: 100 },
          { name: 'San Francisco', lat: 37.77, lng: -122.42, hint: 'Bay Area, Golden Gate', accept: 100 },
          { name: 'Seattle', lat: 47.61, lng: -122.33, hint: 'Emerald City, Pacific NW', accept: 100 },
          { name: 'Miami', lat: 25.76, lng: -80.19, hint: 'South Florida', accept: 100 },
          { name: 'Denver', lat: 39.74, lng: -104.99, hint: 'Mile High City, Colorado', accept: 100 },
          { name: 'Boston', lat: 42.36, lng: -71.06, hint: 'Massachusetts, New England', accept: 100 },
          { name: 'Atlanta', lat: 33.75, lng: -84.39, hint: 'Capital of Georgia', accept: 100 },
        ],
      },
      rivers: {
        label: 'Rivers & Mountains',
        items: [
          { name: 'Mississippi River at St. Louis', lat: 38.63, lng: -90.20, hint: 'Longest river system in N. America', accept: 150 },
          { name: 'Grand Canyon', lat: 36.11, lng: -112.11, hint: 'Carved by the Colorado River, Arizona', accept: 150 },
          { name: 'Mount Rushmore', lat: 43.88, lng: -103.46, hint: 'Presidential monument, South Dakota', accept: 150 },
          { name: 'Yellowstone', lat: 44.43, lng: -110.59, hint: 'First national park, Wyoming', accept: 150 },
          { name: 'Niagara Falls', lat: 43.08, lng: -79.07, hint: 'NY/Canada border', accept: 100 },
          { name: 'Columbia River at Portland', lat: 45.52, lng: -122.68, hint: 'Major NW river, Oregon border', accept: 150 },
          { name: 'Mount Rainier', lat: 46.85, lng: -121.76, hint: 'Highest peak in Washington state', accept: 150 },
          { name: 'Great Salt Lake', lat: 41.17, lng: -112.58, hint: 'Largest saltwater lake in Western hemisphere', accept: 150 },
        ],
      },
    },
  },
  china: {
    label: 'China',
    type: 'country',
    bounds: [[18, 73], [54, 135]],
    categories: {
      cities: {
        label: 'Major Cities',
        items: [
          { name: 'Beijing', lat: 39.91, lng: 116.40, hint: 'Capital city, northern China', accept: 100 },
          { name: 'Shanghai', lat: 31.23, lng: 121.47, hint: 'Largest city, east coast', accept: 100 },
          { name: 'Guangzhou', lat: 23.13, lng: 113.26, hint: 'Southern China, Pearl River Delta', accept: 100 },
          { name: 'Shenzhen', lat: 22.54, lng: 114.06, hint: 'Tech hub near Hong Kong', accept: 100 },
          { name: 'Chengdu', lat: 30.57, lng: 104.07, hint: 'Sichuan province, home of pandas', accept: 120 },
          { name: 'Xi\'an', lat: 34.26, lng: 108.94, hint: 'Ancient capital, Terracotta Army', accept: 120 },
          { name: 'Wuhan', lat: 30.59, lng: 114.31, hint: 'Central China, on the Yangtze', accept: 120 },
          { name: 'Hong Kong', lat: 22.32, lng: 114.17, hint: 'Special Administrative Region', accept: 80 },
          { name: 'Lhasa', lat: 29.65, lng: 91.11, hint: 'Capital of Tibet', accept: 150 },
          { name: 'Urumqi', lat: 43.83, lng: 87.62, hint: 'Capital of Xinjiang, far NW', accept: 200 },
        ],
      },
      rivers: {
        label: 'Rivers & Mountains',
        items: [
          { name: 'Yangtze at Chongqing', lat: 29.56, lng: 106.55, hint: 'Longest river in Asia', accept: 150 },
          { name: 'Yellow River at Lanzhou', lat: 36.06, lng: 103.83, hint: 'Cradle of Chinese civilization', accept: 150 },
          { name: 'Great Wall at Badaling', lat: 40.36, lng: 116.01, hint: 'Most visited section near Beijing', accept: 100 },
          { name: 'Mount Everest (North Face)', lat: 28.00, lng: 86.93, hint: 'Tibet side of the highest peak', accept: 200 },
          { name: 'Three Gorges Dam', lat: 30.82, lng: 111.00, hint: 'Largest hydroelectric dam in the world', accept: 150 },
          { name: 'Li River at Guilin', lat: 25.27, lng: 110.29, hint: 'Famous karst landscape', accept: 120 },
          { name: 'Mount Tai', lat: 36.25, lng: 117.10, hint: 'Most sacred of the Five Great Mountains', accept: 120 },
          { name: 'Pearl River at Guangzhou', lat: 23.10, lng: 113.30, hint: 'Third longest river in China', accept: 120 },
        ],
      },
    },
  },
  uk: {
    label: 'United Kingdom',
    type: 'country',
    bounds: [[49.5, -8], [59, 2]],
    categories: {
      cities: {
        label: 'Cities',
        items: [
          { name: 'London', lat: 51.51, lng: -0.13, hint: 'Capital, on the Thames', accept: 50 },
          { name: 'Edinburgh', lat: 55.95, lng: -3.19, hint: 'Capital of Scotland', accept: 50 },
          { name: 'Cardiff', lat: 51.48, lng: -3.18, hint: 'Capital of Wales', accept: 50 },
          { name: 'Belfast', lat: 54.60, lng: -5.93, hint: 'Capital of Northern Ireland', accept: 50 },
          { name: 'Manchester', lat: 53.48, lng: -2.24, hint: 'Industrial city, NW England', accept: 50 },
          { name: 'Birmingham', lat: 52.49, lng: -1.89, hint: 'Second largest city, central England', accept: 50 },
          { name: 'Liverpool', lat: 53.41, lng: -2.98, hint: 'Port city, home of The Beatles', accept: 50 },
          { name: 'Glasgow', lat: 55.86, lng: -4.25, hint: 'Largest city in Scotland', accept: 50 },
          { name: 'Bristol', lat: 51.45, lng: -2.59, hint: 'SW England, on the Avon', accept: 50 },
          { name: 'Oxford', lat: 51.75, lng: -1.25, hint: 'Famous university city', accept: 40 },
        ],
      },
      rivers: {
        label: 'Rivers & Landmarks',
        items: [
          { name: 'Thames at London', lat: 51.50, lng: -0.12, hint: 'Longest river in England', accept: 40 },
          { name: 'River Severn at Gloucester', lat: 51.86, lng: -2.24, hint: 'Longest river in the UK', accept: 50 },
          { name: 'Loch Ness', lat: 57.32, lng: -4.45, hint: 'Famous Scottish lake', accept: 50 },
          { name: 'Lake District', lat: 54.45, lng: -3.08, hint: 'National park in NW England', accept: 50 },
          { name: 'Ben Nevis', lat: 56.80, lng: -5.00, hint: 'Highest peak in the UK, Scotland', accept: 50 },
          { name: 'Snowdon', lat: 53.07, lng: -4.08, hint: 'Highest peak in Wales', accept: 50 },
          { name: 'White Cliffs of Dover', lat: 51.13, lng: 1.34, hint: 'SE coast, facing France', accept: 40 },
          { name: 'Stonehenge', lat: 51.18, lng: -1.83, hint: 'Ancient stone circle, Wiltshire', accept: 40 },
        ],
      },
    },
  },
  australia: {
    label: 'Australia',
    type: 'country',
    bounds: [[-44, 112], [-10, 155]],
    categories: {
      cities: {
        label: 'Cities & Territories',
        items: [
          { name: 'Sydney', lat: -33.87, lng: 151.21, hint: 'Largest city, New South Wales', accept: 100 },
          { name: 'Melbourne', lat: -37.81, lng: 144.96, hint: 'Second largest, Victoria', accept: 100 },
          { name: 'Brisbane', lat: -27.47, lng: 153.03, hint: 'Capital of Queensland', accept: 100 },
          { name: 'Perth', lat: -31.95, lng: 115.86, hint: 'Western Australia, isolated metropolis', accept: 150 },
          { name: 'Adelaide', lat: -34.93, lng: 138.60, hint: 'Capital of South Australia', accept: 100 },
          { name: 'Canberra', lat: -35.28, lng: 149.13, hint: 'National capital (ACT)', accept: 80 },
          { name: 'Hobart', lat: -42.88, lng: 147.33, hint: 'Capital of Tasmania', accept: 120 },
          { name: 'Darwin', lat: -12.46, lng: 130.84, hint: 'Capital of Northern Territory', accept: 150 },
          { name: 'Gold Coast', lat: -28.02, lng: 153.43, hint: 'Beach city, SE Queensland', accept: 100 },
          { name: 'Cairns', lat: -16.92, lng: 145.77, hint: 'Gateway to the Great Barrier Reef', accept: 120 },
        ],
      },
      rivers: {
        label: 'Rivers & Landmarks',
        items: [
          { name: 'Uluru', lat: -25.34, lng: 131.04, hint: 'Sacred sandstone rock, NT', accept: 150 },
          { name: 'Great Barrier Reef', lat: -18.29, lng: 147.70, hint: 'Largest coral reef system', accept: 200 },
          { name: 'Murray River at Mildura', lat: -34.19, lng: 142.16, hint: 'Longest river in Australia', accept: 150 },
          { name: 'Blue Mountains', lat: -33.72, lng: 150.31, hint: 'Mountain range near Sydney', accept: 100 },
          { name: 'Kakadu National Park', lat: -12.84, lng: 132.87, hint: 'Northern Territory, Aboriginal heritage', accept: 200 },
          { name: 'Twelve Apostles', lat: -38.66, lng: 143.10, hint: 'Great Ocean Road, Victoria', accept: 100 },
          { name: 'Lake Eyre', lat: -28.37, lng: 137.37, hint: 'Lowest point in Australia', accept: 200 },
          { name: 'Mount Kosciuszko', lat: -36.46, lng: 148.26, hint: 'Highest peak in Australia', accept: 120 },
        ],
      },
    },
  },
};

// Flat list of regions for the menu
const REGION_GROUPS = [
  { group: 'World', items: [{ key: 'world', ...QUIZ_REGIONS.world }] },
  { group: 'Continents', items: ['asia', 'europe', 'africa', 'north-america', 'south-america'].map(k => ({ key: k, ...QUIZ_REGIONS[k] })) },
  { group: 'Countries', items: ['india', 'usa', 'china', 'uk', 'australia'].map(k => ({ key: k, ...QUIZ_REGIONS[k] })) },
];

// Haversine distance in km
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Bounds controller — fits map to the region
function MapBoundsController({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [20, 20], duration: 1 });
  }, [bounds, map]);
  return null;
}

function MapPointingQuiz() {
  const [region, setRegion] = useState('world');
  const [category, setCategory] = useState('');
  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [phase, setPhase] = useState('menu'); // menu | playing | result | finished
  const [score, setScore] = useState(0);
  const [clickCoords, setClickCoords] = useState(null);
  const [distance, setDistance] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [mapLayer, setMapLayer] = useState('street');

  const totalQ = 5;

  // When region changes, reset category to first available
  const regionData = QUIZ_REGIONS[region];
  const categoryKeys = regionData ? Object.keys(regionData.categories) : [];

  useEffect(() => {
    if (categoryKeys.length > 0 && (!category || !categoryKeys.includes(category))) {
      setCategory(categoryKeys[0]);
    }
  }, [region]); // eslint-disable-line react-hooks/exhaustive-deps

  const startQuiz = useCallback(() => {
    const rd = QUIZ_REGIONS[region];
    if (!rd || !rd.categories[category]) return;
    const items = [...rd.categories[category].items];
    for (let i = items.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [items[i], items[j]] = [items[j], items[i]]; }
    setQuestions(items.slice(0, Math.min(totalQ, items.length)));
    setQIdx(0);
    setScore(0);
    setPhase('playing');
    setClickCoords(null);
    setDistance(null);
    setShowHint(false);
  }, [region, category]);

  const currentQ = questions[qIdx];
  const tileLayer = TILE_LAYERS[mapLayer];

  const handleMapClick = useCallback((latlng) => {
    if (phase !== 'playing' || !currentQ) return;
    const { lat, lng } = latlng;
    setClickCoords({ lat, lng });
    const dist = haversineKm(lat, lng, currentQ.lat, currentQ.lng);
    setDistance(Math.round(dist));
    const isCorrect = dist <= currentQ.accept;
    if (isCorrect) setScore(s => s + 1);
    setPhase('result');
  }, [phase, currentQ]);

  const nextQuestion = () => {
    if (qIdx + 1 >= questions.length) {
      setPhase('finished');
    } else {
      setQIdx(i => i + 1);
      setPhase('playing');
      setClickCoords(null);
      setDistance(null);
      setShowHint(false);
    }
  };

  // ── Menu screen ──────────────────────────────────────────────────────────
  if (phase === 'menu') {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, height: '100%', overflow: 'auto' }}>
        <QuizIcon sx={{ fontSize: 44, color: 'primary.main', mt: 1 }} />
        <Typography variant="h5" fontWeight={800} textAlign="center">Map Pointing Quiz</Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 440 }}>
          Select a region and category below. You'll be shown a name — click where you think it is on the map!
        </Typography>

        {/* Region selector — grouped */}
        <Box sx={{ width: '100%', maxWidth: 500, mt: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Select Region</Typography>
          {REGION_GROUPS.map(grp => (
            <Box key={grp.group} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ pl: 0.5 }}>{grp.group}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {grp.items.map(r => (
                  <Chip
                    key={r.key}
                    label={r.label}
                    size="small"
                    color={region === r.key ? 'primary' : 'default'}
                    variant={region === r.key ? 'filled' : 'outlined'}
                    onClick={() => setRegion(r.key)}
                    sx={{ cursor: 'pointer', fontWeight: region === r.key ? 700 : 400 }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Category selector */}
        {regionData && (
          <Box sx={{ width: '100%', maxWidth: 500 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Category — {regionData.label}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {categoryKeys.map(ck => (
                <Chip
                  key={ck}
                  label={regionData.categories[ck].label}
                  size="small"
                  color={category === ck ? 'secondary' : 'default'}
                  variant={category === ck ? 'filled' : 'outlined'}
                  onClick={() => setCategory(ck)}
                  sx={{ cursor: 'pointer', fontWeight: category === ck ? 700 : 400 }}
                />
              ))}
            </Box>
            {category && regionData.categories[category] && (
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                {regionData.categories[category].items.length} locations available
              </Typography>
            )}
          </Box>
        )}

        <Button
          variant="contained"
          size="large"
          onClick={startQuiz}
          startIcon={<PlaceIcon />}
          disabled={!category || !regionData?.categories[category]}
          sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, px: 4, mt: 1 }}
        >
          Start Quiz ({Math.min(totalQ, regionData?.categories[category]?.items?.length || totalQ)} questions)
        </Button>
      </Box>
    );
  }

  // ── Finished screen ──────────────────────────────────────────────────────
  if (phase === 'finished') {
    const pct = Math.round((score / questions.length) * 100);
    const emoji = pct >= 80 ? '🏆' : pct >= 50 ? '👍' : '📚';
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, height: '100%', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: 48 }}>{emoji}</Typography>
        <Typography variant="h5" fontWeight={800}>Quiz Complete!</Typography>
        <Typography variant="h3" fontWeight={900} color="primary.main">{score}/{questions.length}</Typography>
        <Typography variant="body1" color="text.secondary">
          {pct >= 80 ? 'Outstanding geography skills!' : pct >= 50 ? 'Good job! Keep exploring the world.' : 'Keep practicing — you\'ll improve!'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button variant="outlined" startIcon={<RetryIcon />} onClick={startQuiz} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Play Again
          </Button>
          <Button variant="contained" onClick={() => setPhase('menu')} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Change Region
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Playing / Result screen with region-bounded Leaflet map ──────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Top bar */}
      <Paper elevation={0} sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Chip label={`${qIdx + 1} / ${questions.length}`} size="small" color="primary" sx={{ fontWeight: 700 }} />
        <Box sx={{ flex: 1, minWidth: 120 }}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
            Point to: <Box component="span" sx={{ color: 'primary.main' }}>{currentQ?.name}</Box>
          </Typography>
          {showHint && currentQ?.hint && (
            <Typography variant="caption" color="text.secondary">Hint: {currentQ.hint}</Typography>
          )}
        </Box>
        <Chip label={`Score: ${score}`} size="small" icon={<TrophyIcon sx={{ fontSize: 14 }} />} sx={{ fontWeight: 600 }} />
        {phase === 'playing' && !showHint && (
          <Chip label="Show Hint" size="small" variant="outlined" onClick={() => setShowHint(true)} sx={{ cursor: 'pointer' }} />
        )}
      </Paper>

      <LinearProgress variant="determinate" value={((qIdx + (phase === 'result' ? 1 : 0)) / questions.length) * 100} sx={{ height: 3 }} />

      {/* Leaflet map — bounded to selected region */}
      <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
        <MapContainer
          center={[20, 0]}
          zoom={2}
          style={{ width: '100%', height: '100%' }}
          zoomControl={true}
          attributionControl={false}
        >
          <TileLayer url={tileLayer.url} attribution={tileLayer.attribution} />
          <MapBoundsController bounds={regionData?.bounds} />
          {phase === 'playing' && <QuizClickHandler onClick={handleMapClick} />}

          {/* User's click marker */}
          {clickCoords && (
            <Marker position={[clickCoords.lat, clickCoords.lng]} icon={redIcon}>
              <Popup>Your guess</Popup>
            </Marker>
          )}

          {/* Correct location marker (shown on result) */}
          {phase === 'result' && currentQ && (
            <Marker position={[currentQ.lat, currentQ.lng]} icon={greenIcon}>
              <Popup>{currentQ.name}</Popup>
            </Marker>
          )}
        </MapContainer>

        {/* Map layer selector overlay */}
        <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1000 }}>
          <ToggleButtonGroup
            value={mapLayer}
            exclusive
            onChange={(e, v) => v && setMapLayer(v)}
            size="small"
            sx={{ bgcolor: 'white', boxShadow: 2, '& .MuiToggleButton-root': { px: 1, py: 0.5, fontSize: '0.7rem' } }}
          >
            {Object.entries(TILE_LAYERS).map(([key, layer]) => (
              <ToggleButton key={key} value={key}>
                {layer.icon}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Region label badge */}
        <Box sx={{ position: 'absolute', top: 10, left: 50, zIndex: 1000 }}>
          <Chip
            label={`${regionData?.label} — ${regionData?.categories[category]?.label || ''}`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.92)', fontWeight: 600, boxShadow: 1 }}
          />
        </Box>

        {/* Result overlay */}
        {phase === 'result' && distance != null && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 1001,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
          }}>
            <Paper sx={{ p: 3, borderRadius: 3, textAlign: 'center', maxWidth: 340, mx: 2 }}>
              {distance <= currentQ.accept ? (
                <>
                  <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                  <Typography variant="h6" fontWeight={800} color="success.main">Correct!</Typography>
                </>
              ) : (
                <>
                  <WrongIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
                  <Typography variant="h6" fontWeight={800} color="error.main">Not quite!</Typography>
                </>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 0.5 }}>
                Your click was <strong>{distance.toLocaleString()} km</strong> from {currentQ.name}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                (Accepted within {currentQ.accept} km)
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" endIcon={qIdx + 1 >= questions.length ? <TrophyIcon /> : <NextIcon />}
                  onClick={nextQuestion} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                  {qIdx + 1 >= questions.length ? 'See Results' : 'Next Question'}
                </Button>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Interactive Leaflet Map for Globe Explorer tab
// ─────────────────────────────────────────────────────────────────────────────
function LeafletExplorer({ markers, selectedLocation, mapCenter, zoom, mapLayer, onMapLayerChange, isMobile }) {
  const tileLayer = TILE_LAYERS[mapLayer];

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <MapContainer
        center={[mapCenter.lat, mapCenter.lng]}
        zoom={zoom}
        style={{ width: '100%', height: '100%' }}
        zoomControl={!isMobile}
        attributionControl={false}
      >
        <TileLayer url={tileLayer.url} attribution={tileLayer.attribution} />
        <MapFlyTo center={mapCenter} zoom={zoom} />

        {markers.map((loc, idx) => (
          <Marker key={`${loc.name}-${idx}`} position={[loc.lat, loc.lng]}>
            <Popup>
              <strong>{loc.name}</strong>
              {loc.info && <><br /><span style={{ fontSize: '0.85em', color: '#666' }}>{loc.info}</span></>}
              <br /><span style={{ fontSize: '0.8em', color: '#999' }}>
                {loc.lat.toFixed(4)}°, {loc.lng.toFixed(4)}°
              </span>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Map type toggle overlay */}
      <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1000 }}>
        <ToggleButtonGroup
          value={mapLayer}
          exclusive
          onChange={(e, v) => v && onMapLayerChange(v)}
          size="small"
          sx={{
            bgcolor: 'rgba(255,255,255,0.95)',
            boxShadow: 2,
            borderRadius: 1,
            '& .MuiToggleButton-root': { px: 1, py: 0.5, fontSize: '0.7rem', gap: 0.3 },
          }}
        >
          {Object.entries(TILE_LAYERS).map(([key, layer]) => (
            <ToggleButton key={key} value={key}>
              {layer.icon}
              {!isMobile && <span>{layer.label}</span>}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main GlobeViewer component
// ─────────────────────────────────────────────────────────────────────────────
function GlobeViewer({ open, onClose, user, vyonnContext, fullScreen = false, dialogProps = {} }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 20, lng: 0 });
  const [zoom, setZoom] = useState(2);
  const [showSidebar, setShowSidebar] = useState(false);
  const [mapLayer, setMapLayer] = useState('street');
  const [userMarkers, setUserMarkers] = useState([]);   // markers added by user search / AI
  const [searchLoading, setSearchLoading] = useState(false);

  // AI Chat state
  const [question, setQuestion] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  // Handle context from Hub Chat
  useEffect(() => {
    if (vyonnContext && open) {
      setQuestion(vyonnContext.question);
      setActiveTab(1);
      setChatHistory([{
        role: 'assistant',
        content: `Welcome from Hub Chat! I see you're interested in:\n\n"${vyonnContext.question}"\n\nLet me help you with that geography question!${vyonnContext.vyonnResponse ? '\n\nPrevious discussion: ' + vyonnContext.vyonnResponse.substring(0, 200) + (vyonnContext.vyonnResponse.length > 200 ? '...' : '') : ''}`,
        timestamp: Date.now()
      }]);
    }
  }, [vyonnContext, open]);

  const userName = user?.displayName?.split(' ')[0] || 'You';
  const userPhoto = user?.photoURL;

  const GEOGRAPHY_QUESTIONS = [
    "Tell me about the Amazon rainforest",
    "What causes earthquakes?",
    "Explain climate zones",
    "Where is Mount Everest?"
  ];

  // Educational + Indian locations
  const educationalLocations = [
    { name: 'Great Wall of China', lat: 40.4319, lng: 116.5704, info: 'Ancient fortification, 13,171 miles long' },
    { name: 'Pyramids of Giza', lat: 29.9792, lng: 31.1342, info: 'Ancient Egyptian monuments, built ~2560 BCE' },
    { name: 'Machu Picchu', lat: -13.1631, lng: -72.5450, info: '15th-century Incan citadel in Peru' },
    { name: 'Taj Mahal', lat: 27.1751, lng: 78.0421, info: 'Mughal mausoleum, built 1632-1653' },
    { name: 'Colosseum', lat: 41.8902, lng: 12.4922, info: 'Ancient Roman amphitheater, built 70-80 CE' },
  ];

  const indianCities = [
    { name: 'New Delhi', lat: 28.6139, lng: 77.2090, info: 'Capital of India' },
    { name: 'Mumbai', lat: 19.0760, lng: 72.8777, info: 'Financial capital of India' },
    { name: 'Kolkata', lat: 22.5726, lng: 88.3639, info: 'Cultural capital of India' },
    { name: 'Chennai', lat: 13.0827, lng: 80.2707, info: 'Gateway to South India' },
    { name: 'Bangalore', lat: 12.9716, lng: 77.5946, info: 'Silicon Valley of India' },
    { name: 'Hyderabad', lat: 17.3850, lng: 78.4867, info: 'City of Pearls' },
  ];

  // All markers = educational + indian + user-added
  const allMarkers = useMemo(() => [
    ...educationalLocations,
    ...indianCities,
    ...userMarkers,
  ], [userMarkers]);

  // Focus on location
  const focusLocation = (location) => {
    setSelectedLocation(location);
    setMapCenter({ lat: location.lat, lng: location.lng });
    setZoom(10);
  };

  // Search location with Nominatim geocoding
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`
      );
      const data = await response.json();

      if (data.length > 0) {
        const result = data[0];
        const newLocation = {
          name: result.display_name.split(',')[0],
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon),
          info: result.display_name,
          userAdded: true,
        };
        // Add to user markers if not already there
        setUserMarkers(prev => {
          if (prev.some(m => m.name === newLocation.name)) return prev;
          return [...prev, newLocation];
        });
        focusLocation(newLocation);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  // AI Chat
  const askGeographyAI = async () => {
    if (!question.trim()) return;

    setLoading(true);
    const userQuestion = question;
    setQuestion('');
    setChatHistory(prev => [{ role: 'user', content: userQuestion }, ...prev]);

    try {
      const hasDevanagari = /[\u0900-\u097F]/.test(userQuestion);
      const hasTelugu = /[\u0C00-\u0C7F]/.test(userQuestion);
      const hasTamil = /[\u0B80-\u0BFF]/.test(userQuestion);
      const hasKannada = /[\u0C80-\u0CFF]/.test(userQuestion);
      const hasMalayalam = /[\u0D00-\u0D7F]/.test(userQuestion);

      const isRegional = hasDevanagari || hasTelugu || hasTamil || hasKannada || hasMalayalam;
      const lang = hasTelugu ? 'Telugu (తెలుగు)' :
                   hasDevanagari ? 'Hindi (हिंदी)' :
                   hasTamil ? 'Tamil (தமிழ்)' :
                   hasKannada ? 'Kannada (ಕನ್ನಡ)' :
                   hasMalayalam ? 'Malayalam (മലయാളം)' : 'English';

      const prompt = `You are Vyonn Globe, a brilliant and friendly geography tutor.

${isRegional ? `IMPORTANT: Student asked in ${lang}. You MUST respond in ${lang}!` : ''}

Student asked: "${userQuestion}"

Provide a clear, educational response that:
1. Explains the geography concept clearly ${isRegional ? `(in ${lang})` : ''}
2. Includes interesting facts and context ${isRegional ? `(in ${lang})` : ''}
3. Relates to real-world locations when relevant
4. Is encouraging and supportive ${isRegional ? `(in ${lang})` : ''}
5. Suggests related topics to explore ${isRegional ? `(in ${lang})` : ''}

When mentioning specific geographic locations (cities, landmarks, countries, natural features), format them as:
[[LocationName|latitude|longitude]]

For example:
- "[[Mount Everest|27.9881|86.9250]] is the highest peak..."
- "Visit [[Paris|48.8566|2.3522]] to see the Eiffel Tower"
- "The [[Amazon Rainforest|-3.4653|-62.2159]] spans multiple countries"

${isRegional ? `Write your ENTIRE response in ${lang} using proper Unicode! Location names can be in original language.` : 'You can include multiple locations in your response. Be warm and engaging!'}`;

      const response = await callLLM(prompt, { feature: 'general', temperature: 0.7, maxTokens: 2048 });

      // Parse location links
      const locations = [];
      const locationRegex = /\[\[([^\|]+)\|([-\d.]+)\|([-\d.]+)\]\]/g;
      let match;

      while ((match = locationRegex.exec(response || '')) !== null) {
        locations.push({
          name: match[1].trim(),
          lat: parseFloat(match[2]),
          lng: parseFloat(match[3]),
          placeholder: match[0]
        });
      }

      // Auto-add AI locations to the map markers
      if (locations.length > 0) {
        setUserMarkers(prev => {
          const newMarkers = [...prev];
          locations.forEach(loc => {
            if (!newMarkers.some(m => m.name === loc.name)) {
              newMarkers.push({ name: loc.name, lat: loc.lat, lng: loc.lng, info: `AI-suggested location`, userAdded: true });
            }
          });
          return newMarkers;
        });
      }

      setChatHistory(prev => [{ role: 'assistant', content: response || "Let me help you explore the world!", locations }, ...prev]);
    } catch (error) {
      try {
        setChatHistory(prev => [{
          role: 'assistant',
          content: "Let me help you explore the world!",
          locations: []
        }, ...prev]);
      } catch (innerError) {
        // Failsafe
      }
    } finally {
      try {
        setLoading(false);
      } catch (finalError) {
        // Absolute failsafe
      }
    }
  };

  // Handle location click from AI response — switch to Globe Explorer and fly to location
  const handleViewLocation = (location) => {
    // Add to markers if not present
    setUserMarkers(prev => {
      if (prev.some(m => m.name === location.name)) return prev;
      return [...prev, { ...location, info: 'AI-suggested location', userAdded: true }];
    });
    focusLocation(location);
    setActiveTab(1); // Switch to Globe Explorer
    if (isMobile) setShowSidebar(false);
  };

  const clearUserMarkers = () => {
    setUserMarkers([]);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={fullScreen ? false : "lg"}
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { height: fullScreen ? '100%' : '90vh' } }}
      {...dialogProps}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'linear-gradient(135deg, #0984e3 0%, #0652dd 100%)',
        color: 'white',
        py: 1.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <VyonnGlobeIcon size={36} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Vyonn Globe Explorer</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>Explore World Geography · Landmarks · Cities</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f5f5f5' }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ '& .MuiTab-root': { fontWeight: 600, minWidth: 'auto', px: 2 } }}>
          <Tab icon={<VyonnGlobeIcon size={18} />} label="Ask Vyonn AI" iconPosition="start" />
          <Tab icon={<GlobeIcon sx={{ fontSize: 18 }} />} label="Globe Explorer" iconPosition="start" />
          <Tab icon={<QuizIcon sx={{ fontSize: 18 }} />} label="Map Pointing" iconPosition="start" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Tab 0: AI Chat */}
        {activeTab === 0 && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Paper elevation={0} sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: 'white' }}>
              <TextField
                fullWidth
                multiline
                maxRows={3}
                placeholder="Ask me anything about geography..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), askGeographyAI())}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <VoiceInputButton
                        onTranscript={setQuestion}
                        existingText={question}
                        disabled={loading}
                        size="small"
                      />
                      <IconButton onClick={askGeographyAI} disabled={loading || !question.trim()} color="primary">
                        {loading ? <CircularProgress size={20} /> : <SendIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {GEOGRAPHY_QUESTIONS.map((q, i) => (
                  <Chip key={i} label={q} size="small" onClick={() => setQuestion(q)} sx={{ cursor: 'pointer', bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' }, fontSize: '0.75rem' }} />
                ))}
              </Box>
            </Paper>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#fafafa' }}>
              {chatHistory.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    bgcolor: '#e3f2fd',
                    border: '3px solid',
                    borderColor: '#0984e3',
                    mb: 2,
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.8, transform: 'scale(1.05)' }
                    }
                  }}>
                    <GlobeIcon sx={{ fontSize: 56, color: '#0984e3' }} />
                  </Box>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Start a conversation
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Discover countries, cities, landmarks, and world geography
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
                    Locations mentioned by AI will be auto-plotted on the Globe Explorer map
                  </Typography>
                </Box>
              ) : (
                chatHistory.map((msg, i) => (
                  <Box key={i} sx={{ mb: 2 }}>
                    {msg.role === 'user' ? (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Paper sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, maxWidth: '80%' }}>
                          <Typography variant="body2">{msg.content}</Typography>
                        </Paper>
                        <Avatar src={userPhoto} sx={{ width: 32, height: 32, bgcolor: '#1976d2' }}>
                          {userName[0]}
                        </Avatar>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box sx={{ mt: 0.5 }}>
                          <VyonnGlobeIcon size={32} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Paper sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                            {msg.locations && msg.locations.length > 0 ? (
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {msg.content.split(/(\[\[[^\]]+\]\])/).map((part, idx) => {
                                  const location = msg.locations.find(loc => loc.placeholder === part);
                                  if (location) {
                                    return (
                                      <Typography
                                        key={idx}
                                        component="span"
                                        onClick={() => handleViewLocation(location)}
                                        sx={{
                                          color: 'primary.main',
                                          textDecoration: 'underline',
                                          cursor: 'pointer',
                                          fontWeight: 600,
                                          '&:hover': { color: 'primary.dark' }
                                        }}
                                      >
                                        {location.name}
                                      </Typography>
                                    );
                                  }
                                  return part;
                                })}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                component="div"
                                sx={{
                                  '& p': { margin: '8px 0' },
                                  '& ul': { margin: '8px 0', paddingLeft: 0 },
                                  '& li': { marginLeft: '20px' },
                                  '& h1, & h2, & h3': { marginTop: '12px', marginBottom: '8px' }
                                }}
                                dangerouslySetInnerHTML={{ __html: markdownToHtml(msg.content) }}
                              />
                            )}
                          </Paper>
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))
              )}
              {loading && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Box sx={{ mt: 0.5 }}>
                    <VyonnGlobeIcon size={32} />
                  </Box>
                  <Paper sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" component="span" sx={{ ml: 1 }}>Thinking...</Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Tab 1: Globe Explorer with Leaflet */}
        {activeTab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
          {/* Mobile search bar */}
          {isMobile && (
            <Box sx={{ p: 1, borderBottom: '1px solid #ddd', bgcolor: '#f8f9fa' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  InputProps={{
                    endAdornment: searchLoading ? <CircularProgress size={16} /> : null,
                  }}
                />
                <IconButton onClick={handleSearch} color="primary" size="small" disabled={searchLoading}>
                  <SearchIcon />
                </IconButton>
                <IconButton
                  onClick={() => setShowSidebar(!showSidebar)}
                  color="primary"
                  size="small"
                  sx={{ bgcolor: showSidebar ? '#e3f2fd' : 'transparent' }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            </Box>
          )}

        <Box sx={{ display: 'flex', width: '100%', height: '100%', flex: 1 }}>
        {/* Sidebar */}
        {(!isMobile || showSidebar) && (
        <Box sx={{
          width: isMobile ? '100%' : 280,
          borderRight: isMobile ? 'none' : '1px solid #ddd',
          p: 2,
          overflow: 'auto',
          bgcolor: '#f8f9fa',
          position: isMobile ? 'absolute' : 'relative',
          zIndex: isMobile ? 10 : 1,
          height: '100%',
          boxShadow: isMobile ? 3 : 0
        }}>
          {/* Search (desktop) */}
          {!isMobile && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: searchLoading ? <CircularProgress size={16} /> : null,
              }}
            />
            <IconButton onClick={handleSearch} color="primary" disabled={searchLoading}>
              <SearchIcon />
            </IconButton>
          </Box>
          )}

          {/* User-added markers */}
          {userMarkers.length > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Marked Locations ({userMarkers.length})
                </Typography>
                <Tooltip title="Clear all markers">
                  <IconButton size="small" onClick={clearUserMarkers} sx={{ color: 'text.disabled' }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {userMarkers.map((loc, idx) => (
                  <Chip
                    key={`user-${idx}`}
                    label={loc.name}
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={() => focusLocation(loc)}
                    onDelete={() => setUserMarkers(prev => prev.filter((_, i) => i !== idx))}
                    sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                  />
                ))}
              </Box>
            </>
          )}

          {/* Quick locations */}
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            World Wonders
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {educationalLocations.map((loc) => (
              <Chip
                key={loc.name}
                label={loc.name}
                size="small"
                onClick={() => focusLocation(loc)}
                sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
              />
            ))}
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Indian Cities
          </Typography>
          <List dense sx={{ bgcolor: 'white', borderRadius: 1 }}>
            {indianCities.map((city) => (
              <ListItem
                button
                key={city.name}
                onClick={() => focusLocation(city)}
                selected={selectedLocation?.name === city.name}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <PlaceIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={city.name}
                  secondary={city.info}
                  primaryTypographyProps={{ fontSize: '0.85rem' }}
                  secondaryTypographyProps={{ fontSize: '0.7rem' }}
                />
              </ListItem>
            ))}
          </List>

          {/* Selected location info */}
          {selectedLocation && (
            <Paper sx={{ p: 2, mt: 2, bgcolor: '#e3f2fd' }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {selectedLocation.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedLocation.info}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Lat: {selectedLocation.lat.toFixed(4)}, Lng: {selectedLocation.lng.toFixed(4)}
              </Typography>
            </Paper>
          )}

          {/* Close sidebar on mobile */}
          {isMobile && (
            <IconButton
              onClick={() => setShowSidebar(false)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'white',
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        )}

        {/* Leaflet Map */}
        <Box sx={{ flexGrow: 1, height: '100%', position: 'relative' }}>
          <LeafletExplorer
            markers={allMarkers}
            selectedLocation={selectedLocation}
            mapCenter={mapCenter}
            zoom={zoom}
            mapLayer={mapLayer}
            onMapLayerChange={setMapLayer}
            isMobile={isMobile}
          />
          <Alert
            severity="info"
            sx={{
              position: 'absolute',
              bottom: 10,
              left: 10,
              right: 10,
              opacity: 0.9,
              zIndex: 1000,
            }}
          >
            {isMobile ? 'Search for places or ask Vyonn AI — locations get plotted automatically' : 'Search for any place worldwide or ask Vyonn AI — all locations get highlighted on the map'}
          </Alert>
        </Box>
        </Box>
        </Box>
        )}

        {/* Tab 2: Map Pointing Quiz */}
        {activeTab === 2 && <MapPointingQuiz />}
      </DialogContent>

      {/* Footer */}
      <Box sx={{
        py: 1,
        px: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        textAlign: 'center',
        bgcolor: 'grey.50'
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', lineHeight: 1.4 }}>
          &copy; 2026 Ekamanam AI Innovations | PDF copyrights belong to respective owners | For personal educational use only
        </Typography>
      </Box>
    </Dialog>
  );
}

export default GlobeViewer;
